import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { GridActionsCellItem, GridActionsCellItemProps, GridRowParams, GridColDef } from '@mui/x-data-grid-premium';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { usePermissions } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import {
  cleanFundValuationFrequencies,
  deleteFundValuationFrequency,
  fetchFundValuationFrequencies,
} from '../../redux/modules/fundValuationFrequency/fundValuationFrequency.actions';
import { fundValuationFrequencySelector } from '../../redux/modules/fundValuationFrequency/fundValuationFrequency.selectors';
import { FundValuationFrequency } from '../../types/fundValuationFrequency';
import * as permissions from '../../utils/permissions';
import { FundValuationFrequencyDialog } from './FundValuationFrequencyDialog';

export function FundValuationFrequencyList() {
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const [open, setOpen] = useState(false);
  const [editValue, setEditValue] = useState<FundValuationFrequency | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const canDelete = usePermissions({ items: [permissions.FundValuationFrequency.delete] });
  const canUpdate = usePermissions({ items: [permissions.FundValuationFrequency.update] });
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'nameEn',
      headerName: 'Name En',
      flex: 1,
    },
    {
      field: 'nameAr',
      headerName: 'Name Ar',
      flex: 1,
    },
    {
      field: 'descriptionEn',
      headerName: 'Description En',
      flex: 1,
    },
    {
      field: 'descriptionAr',
      headerName: 'Description Ar',
      flex: 1,
    },
    {
      field: 'expression',
      headerName: 'Expression',
      flex: 1,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      width: 100,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      width: 100,
    },
  ]);

  const { totalFundValuationFrequencies, fundValuationFrequencies, fetchStatus, deleteStatus } =
    useSelector(fundValuationFrequencySelector);

  const onDeleteFundValuationFrequency = (id: number) => {
    dispatch(deleteFundValuationFrequency(id));
  };

  const onEditFundValuationFrequency = (row: FundValuationFrequency): void => {
    setEditValue(row);
    setOpen(true);
  };

  const onAddNew = (): void => {
    setOpen(true);
  };

  const handleClose = (isReload: boolean) => {
    if (isReload) {
      tableRef?.current?.reload();
    }

    setEditValue(null);
    setOpen(false);
  };

  useEffect(
    () => () => {
      dispatch(cleanFundValuationFrequencies());
    },
    [],
  );

  useEffect(() => {
    if (deleteStatus === fetchStatuses.success) {
      handleClose(true);
      enqueueSnackbar('Fund Valuation Frequency deleted!', { variant: 'success' });
    }
    if (deleteStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Fund Valuation Frequency deletion error!', { variant: 'error' });
    }
  }, [deleteStatus]);

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (canDelete) {
          actions.push(
            <GridActionsCellItem
              key={`fundValuationFrequency-list-delete-${params.row.id}`}
              icon={<DeleteOutlineOutlinedIcon />}
              label="Link"
              onClick={() => onDeleteFundValuationFrequency(params.row.id)}
            />,
          );
        }

        if (canUpdate) {
          actions.push(
            <GridActionsCellItem
              key={`fundValuationFrequency-list-update-${params.row.id}`}
              icon={<EditOutlinedIcon />}
              label="Actions"
              onClick={() => onEditFundValuationFrequency(params.row)}
            />,
          );
        }

        return actions;
      },
    };

    if (canDelete || canUpdate) {
      setColumns([actionColumn, ...columns]);
    }
  }, [canDelete, canUpdate]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Fund Valuation Frequencies &nbsp;
          <Fab color="primary" size="small" aria-label="Create Fund ValuationFrequency" onClick={onAddNew}>
            <AddIcon />
          </Fab>
        </Typography>
      </Box>

      <FundValuationFrequencyDialog open={open} onClose={handleClose} initialValue={editValue} />

      <Table
        ref={tableRef}
        fetchItems={fetchFundValuationFrequencies}
        rows={fundValuationFrequencies}
        columns={columns}
        loading={fetchStatus === fetchStatuses.pending}
        rowCount={totalFundValuationFrequencies}
      />
    </Container>
  );
}
